import { z } from "zod";
import type { ItemPayload } from "./items";

/**
 * Student answers per item type and how each is scored (spec §13.4a): 0/1, NCSBN +/− partial credit,
 * per-part credit for multi-part items, and NCSBN rationale scoring for linked blanks.
 * Scoring is server-side truth; the app renders the result it gets back.
 */
const ids = z.array(z.string()).max(50);

export const itemResponseSchema = z.discriminatedUnion("item_type", [
  z.object({ item_type: z.literal("mcq"), choice: z.string() }),
  z.object({ item_type: z.literal("sata"), choices: ids }),
  z.object({ item_type: z.literal("bowtie"), condition: z.string(), actions: ids, parameters: ids }),
  z.object({ item_type: z.literal("matrix"), rows: z.record(z.string(), ids) }),
  z.object({ item_type: z.literal("highlight"), selected: ids }),
  z.object({ item_type: z.literal("cloze"), blanks: z.record(z.string(), z.string()) }),
  z.object({ item_type: z.literal("drag_drop_cloze"), placements: z.record(z.string(), z.string()) }),
  z.object({ item_type: z.literal("trend"), choices: ids }),
]);
export type ItemResponse = z.infer<typeof itemResponseSchema>;

/** A blank response of the right shape for an item, before the student has touched it. */
export function emptyResponse(item: ItemPayload): ItemResponse {
  switch (item.item_type) {
    case "mcq":
      return { item_type: "mcq", choice: "" };
    case "sata":
      return { item_type: "sata", choices: [] };
    case "bowtie":
      return { item_type: "bowtie", condition: "", actions: [], parameters: [] };
    case "matrix":
      return { item_type: "matrix", rows: {} };
    case "highlight":
      return { item_type: "highlight", selected: [] };
    case "cloze":
      return { item_type: "cloze", blanks: {} };
    case "drag_drop_cloze":
      return { item_type: "drag_drop_cloze", placements: {} };
    case "trend":
      return { item_type: "trend", choices: [] };
  }
}

/** True when the student has answered every part the item asks for (the Submit button's gate). */
export function isAnswered(item: ItemPayload, r: ItemResponse): boolean {
  switch (r.item_type) {
    case "mcq":
      return r.choice !== "";
    case "sata":
    case "trend":
      return r.choices.length > 0;
    case "highlight":
      return r.selected.length > 0;
    case "bowtie":
      return r.condition !== "" && r.actions.length === 2 && r.parameters.length === 2;
    case "matrix":
      return item.item_type === "matrix" && item.rows.every((row) => (r.rows[row.id] ?? []).length > 0);
    case "cloze":
      return item.item_type === "cloze" && item.blanks.every((b) => !!r.blanks[b.id]);
    case "drag_drop_cloze":
      return item.item_type === "drag_drop_cloze" && Object.keys(item.key).every((b) => !!r.placements[b]);
  }
}

export interface ItemScore {
  /** 0–1 share of the item's available credit. */
  score: number;
  correct: boolean;
}

const round = (n: number) => Math.round(Math.max(0, Math.min(1, n)) * 1000) / 1000;

/** Multi-select: +/− (one point per right pick, minus one per wrong pick, floor 0) or all-or-nothing. */
function multiSelect(keys: string[], picked: string[], rule: string): ItemScore {
  const chosen = [...new Set(picked)];
  const exact = chosen.length === keys.length && chosen.every((c) => keys.includes(c));
  if (rule === "zero_one") return { score: exact ? 1 : 0, correct: exact };
  const right = chosen.filter((c) => keys.includes(c)).length;
  const wrong = chosen.length - right;
  return { score: round((right - wrong) / keys.length), correct: exact };
}

/** Per-part credit: each part is 0/1; zero_one items need every part right. */
function parts(rightParts: number, totalParts: number, rule: string): ItemScore {
  const all = rightParts === totalParts;
  if (rule === "zero_one") return { score: all ? 1 : 0, correct: all };
  return { score: round(rightParts / totalParts), correct: all };
}

/**
 * Linked-blank credit. With "rationale" each group (dyad/triad) is one point only when all its blanks are right and
 * ungrouped blanks are a point each (NCSBN: both parts of a cause-and-effect pair must be correct). Other rules score
 * blank by blank.
 */
function linkedBlanks(blankIds: string[], isRight: (blank: string) => boolean, rule: string, groups: string[][] | undefined): ItemScore {
  if (rule !== "rationale" || !groups) return parts(blankIds.filter(isRight).length, blankIds.length, rule);
  const grouped = new Set(groups.flat());
  const units = [...groups.map((g) => g.every(isRight)), ...blankIds.filter((b) => !grouped.has(b)).map(isRight)];
  return parts(units.filter(Boolean).length, units.length, rule);
}

export class ResponseMismatchError extends Error {
  constructor() {
    super("The answer doesn't match this question's format");
    this.name = "ResponseMismatchError";
  }
}

export function scoreItem(item: ItemPayload, response: ItemResponse): ItemScore {
  if (item.item_type !== response.item_type) throw new ResponseMismatchError();
  switch (item.item_type) {
    case "mcq": {
      const ok = (response as { choice: string }).choice === item.key;
      return { score: ok ? 1 : 0, correct: ok };
    }
    case "sata":
      return multiSelect(item.keys, (response as { choices: string[] }).choices, item.scoring);
    case "trend":
      return item.answer_format === "mcq"
        ? (() => {
            const ok = (response as { choices: string[] }).choices.length === 1 && item.keys.includes((response as { choices: string[] }).choices[0]!);
            return { score: ok ? 1 : 0, correct: ok };
          })()
        : multiSelect(item.keys, (response as { choices: string[] }).choices, item.scoring);
    case "highlight":
      return multiSelect(item.keys, (response as { selected: string[] }).selected, item.scoring);
    case "bowtie": {
      const r = response as { condition: string; actions: string[]; parameters: string[] };
      const right =
        (r.condition === item.key.condition ? 1 : 0) +
        [...new Set(r.actions)].slice(0, 2).filter((a) => item.key.actions.includes(a)).length +
        [...new Set(r.parameters)].slice(0, 2).filter((p) => item.key.parameters.includes(p)).length;
      return parts(right, 5, item.scoring);
    }
    case "matrix": {
      const r = (response as { rows: Record<string, string[]> }).rows;
      if (item.mode === "single") {
        const right = item.rows.filter((row) => {
          const picked = r[row.id] ?? [];
          return picked.length === 1 && (item.key[row.id] ?? []).includes(picked[0]!);
        }).length;
        return parts(right, item.rows.length, item.scoring);
      }
      // Multiple-response grid: +/− across every cell, like SATA.
      const keys = Object.entries(item.key).flatMap(([row, cols]) => cols.map((c) => `${row}:${c}`));
      const picked = Object.entries(r).flatMap(([row, cols]) => cols.map((c) => `${row}:${c}`));
      return multiSelect(keys, picked, item.scoring);
    }
    case "cloze": {
      const r = (response as { blanks: Record<string, string> }).blanks;
      const key = new Map(item.blanks.map((b) => [b.id, b.key]));
      return linkedBlanks([...key.keys()], (b) => r[b] === key.get(b), item.scoring, item.rationale_groups);
    }
    case "drag_drop_cloze": {
      const r = (response as { placements: Record<string, string> }).placements;
      return linkedBlanks(Object.keys(item.key), (b) => r[b] === item.key[b], item.scoring, item.rationale_groups);
    }
  }
}
